import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { DpdAppBar, EXTERNAL_LINKS } from '@dpdgroupuk/cc-dpd-ui';
import { ROUTE_PATH, STRINGS } from '../../constants';
import { CitysprintWizardActions } from '../../pages/SendCitysprintParcel/redux';
import { AUTH } from '../../constants/strings';
import { DPDLogo } from '../icons';
import { useAuth } from '../../features/Auth/Context';
import { BasketSelectors } from '../../redux/basket';
import {
  EXTERNAL_NAV_ITEMS,
  SECONDARY_NAV_BY_PRIMARY,
} from '../../constants/navItems';
import configSlice from '../../redux/configSlice';

const StyledLogoText = styled(Typography)(({ theme }) => ({
  textTransform: 'none',
  color: theme.vars.palette.text.primary,
}));

const DpdOnlineLogo = () => {
  const theme = useTheme();
  const darkSelector = theme.getColorSchemeSelector('dark');

  return (
    <>
      <DPDLogo
        sx={{
          fontSize: 52,
          [darkSelector]: { filter: 'brightness(0) invert(1)' },
        }}
      />
      <Divider
        orientation='vertical'
        sx={{
          mx: 1,
          borderRightWidth: 2,
          height: 26,
          alignSelf: 'center',
        }}
      />
      <StyledLogoText noWrap>{STRINGS.DPD_ONLINE}</StyledLogoText>
    </>
  );
};

const CITYSPRINT_NAV_RESET_STATE = { citysprintNavReset: true };

function HomeAppBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentSession, signOut } = useAuth();
  const isAuthenticated = !!currentSession?.uid;
  const basketItems = useSelector(BasketSelectors.getBasketItems);
  const basketCount = basketItems?.length ?? 0;
  const { sameday } = useSelector(configSlice.selectors.getFeatureConfig);
  const navItems = useMemo(() => EXTERNAL_NAV_ITEMS.map(id => id), []);

  const navigateToCitysprintParcel = useCallback(() => {
    dispatch(CitysprintWizardActions.clear());
    navigate(ROUTE_PATH.SEND_A_CITYSPRINT_PARCEL, {
      replace: true,
      state: CITYSPRINT_NAV_RESET_STATE,
    });
  }, [dispatch, navigate]);

  const handleSecondaryNavClick = useCallback(
    path => {
      if (path === ROUTE_PATH.SEND_A_CITYSPRINT_PARCEL) {
        navigateToCitysprintParcel();
        return;
      }
      navigate(path);
    },
    [navigate, navigateToCitysprintParcel]
  );

  const getActivePrimaryNav = () => {
    for (const primaryKey of EXTERNAL_NAV_ITEMS) {
      const secondaryItems = SECONDARY_NAV_BY_PRIMARY[primaryKey];
      if (!secondaryItems) {
        continue;
      }
      const hasMatch = secondaryItems.some(item => {
        const path = item.path || item.children?.[0]?.path;

        // If there's no path to check (e.g. Products & Services container), skip it
        if (!path) {
          return false;
        }
        if (path === '/') {
          return pathname === '/';
        }
        return pathname.startsWith(path);
      });
      if (hasMatch) {
        return primaryKey;
      }
    }
    return 'send';
  };

  const isSecondaryItemActive = (item, fallbackPath) => {
    if (item.matchPaths) {
      return item.matchPaths.some(p => pathname.startsWith(p));
    }
    const path = item.path || fallbackPath || '';
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  };

  const externalUrlByPrimary = {
    return: () => {
      window.location.href = `${EXTERNAL_LINKS.RETURNS_URL}/`;
    },
    businessAccount: () => {
      window.location.href = `${EXTERNAL_LINKS.BUSINESS_URL}/`;
    },
    track: () => {
      window.location.href = `${EXTERNAL_LINKS.TRACKING_URL}/`;
    },
    pickupFinder: () => {
      window.location.href = `${EXTERNAL_LINKS.PICKUP_URL}/`;
    },
  };

  const secondaryNavByPrimary = Object.fromEntries(
    Object.entries(SECONDARY_NAV_BY_PRIMARY).map(([primaryKey, items]) => [
      primaryKey,
      items
        // Hide Citysprint when sameday feature flag is false
        .filter(item => sameday || item.id !== 'send-a-citysprint-parcel')
        .map(item => {
          if (item.expandable) {
            return {
              ...item,
              active: isSecondaryItemActive(item, '/products-and-services'),
            };
          }
          const externalOnClick = externalUrlByPrimary[primaryKey];
          if (externalOnClick) {
            return {
              ...item,
              onClick: externalOnClick,
              active: isSecondaryItemActive(item, ''),
            };
          }
          if (item.onClick) {
            return {
              ...item,
              active: isSecondaryItemActive(item, ''),
            };
          }

          if (item.id === 'send-a-citysprint-parcel') {
            return {
              ...item,
              onClick: navigateToCitysprintParcel,
              active: isSecondaryItemActive(item, ''),
            };
          }

          return {
            ...item,
            onClick: () => navigate(item.path),
            active: isSecondaryItemActive(item, ''),
          };
        }),
    ])
  );

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error(AUTH.LOGOUT_FAILED, error);
    }
  };

  return (
    <DpdAppBar
      logo={<DpdOnlineLogo />}
      showBasket
      showAccount
      basketCount={basketCount}
      isAuthenticated={isAuthenticated}
      onSignOut={handleLogout}
      navItems={navItems}
      secondaryNavByPrimary={secondaryNavByPrimary}
      activePrimaryId={getActivePrimaryNav()}
      onSecondaryItemClick={handleSecondaryNavClick}
    />
  );
}

export default HomeAppBar;
